import { invoke } from "@tauri-apps/api/core";
import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import { env } from "../env";
import { $documentEnvironmentRawVars, $environmentNames, $environmentVars } from "../environments";
import { $httpRequestPage } from "../request-page/request-page.atom";
import { requestPageKeyForDocsRoute } from "../request-page/requestPageKey";
import {
	type EnvVarHint,
	findEnvVarReferences,
	findEnvVarReferencesWithHints,
} from "../request-page/substitute-variables";
import {
	$documentSecretsproxyContext,
	$workspaceSecretNamesState,
	$workspaceSecretProfiles,
} from "../secretsproxy";

const logger = new Logger("document-required-vars");

// =============================================================================
// Types
// =============================================================================

export type DocumentRequiredVarsEnvironmentStatus = {
	/** All env variable names found across all request pages in the document. */
	required: string[];
	/** Variables that have values in this environment. */
	resolved: string[];
	/** Variables that are required but not yet set in this environment. */
	missing: string[];
};

export type DocumentRequiredVarsStatus = Record<string, DocumentRequiredVarsEnvironmentStatus>;

// =============================================================================
// Atoms
// =============================================================================

/**
 * Extracts all {{env.X}} variable names referenced across all request pages in
 * a document.
 *
 * Returns a deduplicated sorted array of variable names. Pure extraction —
 * does not check whether values are resolved.
 */
export const $documentEnvVarReferences = atomFamily((documentId: string) => {
	const refsAtom = atom((get): string[] => {
		const metadataKey = metadataKeyForDocsRoute(documentId);
		const metadata = get($documentMetadata(metadataKey));

		if (!metadata) {
			return [];
		}

		const allRefs = new Set<string>();

		for (const page of metadata.pagesMetadata) {
			if (page.type !== "request") {
				continue;
			}

			const requestPageKey = requestPageKeyForDocsRoute({ documentId, pageId: page.id });
			const requestPage = get($httpRequestPage(requestPageKey));

			if (!requestPage?.request?.request) {
				continue;
			}

			for (const varName of findEnvVarReferences(requestPage.request.request)) {
				allRefs.add(varName);
			}
		}

		return Array.from(allRefs).sort();
	});
	refsAtom.debugLabel = `documentEnvVarReferences/${documentId}`;
	return refsAtom;
});

export type DocumentSecretReference = {
	documentId: string;
	title: string;
};

// Raw shape returned by the Rust `scan_documents_for_secret_ref` command.
type RustSecretDocumentRef = { document_id: string; title: string };

/**
 * Scan every local document directory for `{{env.NAME}}`
 * references by reading its YAML files directly. This runs in Rust so it
 * doesn't need each document's metadata to be loaded into memory — it sees
 * every doc that has ever been cloned/saved locally.
 *
 * Remote-only documents that have never been cloned on this device cannot
 * be scanned (their content lives on the server). The delete-secret
 * confirmation surfaces this caveat in copy.
 */
export const scanDocumentsReferencingSecret = async (
	name: string
): Promise<DocumentSecretReference[]> => {
	if (!name) {
		return [];
	}
	if (!env.isDesktopApp) {
		return [];
	}
	try {
		const results = await invoke<RustSecretDocumentRef[]>("scan_documents_for_secret_ref", {
			secretName: name,
		});
		return results.map((r) => ({ documentId: r.document_id, title: r.title }));
	} catch (error) {
		logger.error("Failed to scan documents for secret ref", { name, cause: error });
		return [];
	}
};

/**
 * Env refs in the document that are not defined locally and are also not
 * defined in the given workspace + profile.
 */
export const $documentMissingSecrets = atomFamily(
	(params: { documentId: string; workspaceSlug: string; profile: string }) => {
		const missingAtom = atom(async (get): Promise<string[]> => {
			const refs = get($documentEnvVarReferences(params.documentId));
			if (refs.length === 0) {
				return [];
			}

			const globalVars = await get($environmentVars(params.profile));
			const rawDocVars = await get(
				$documentEnvironmentRawVars({
					documentId: params.documentId,
					environmentName: params.profile,
				})
			);
			const localVars = { ...globalVars, ...rawDocVars };

			const secretNamesState = await get(
				$workspaceSecretNamesState({
					workspaceSlug: params.workspaceSlug,
					profile: params.profile,
				})
			);

			if (secretNamesState.status !== "ok") {
				return [];
			}

			const defined = new Set(secretNamesState.names);
			return refs.filter((name) => {
				if (name in localVars && localVars[name] !== "") {
					return false;
				}
				return !defined.has(name);
			});
		});
		missingAtom.debugLabel = `documentMissingSecrets/${params.documentId}`;
		return missingAtom;
	},
	isEqual
);

/**
 * Per-environment resolution status for all {{env.X}} variables required by a document.
 *
 * Scans all request pages in the document, collects every {{env.X}} reference,
 * then compares against the merged environment vars (document-scoped + global)
 * for each known environment.
 *
 * Shape:
 * ```
 * {
 *   "default":    { required: ["API_KEY", "BASE_URL"], resolved: ["BASE_URL"], missing: ["API_KEY"] },
 *   "production": { required: ["API_KEY", "BASE_URL"], resolved: ["API_KEY", "BASE_URL"], missing: [] },
 * }
 * ```
 */
export const $documentRequiredVars = atomFamily((documentId: string) => {
	const statusAtom = atom(async (get): Promise<DocumentRequiredVarsStatus> => {
		const required = get($documentEnvVarReferences(documentId));

		const environmentNames = new Set(await get($environmentNames));
		const defaultSecretsContext = await get(
			$documentSecretsproxyContext({ documentId, environmentName: "default" })
		);
		if (defaultSecretsContext) {
			const workspaceProfiles = await get(
				$workspaceSecretProfiles(defaultSecretsContext.workspace_slug)
			);
			workspaceProfiles.forEach((profile) => environmentNames.add(profile));
		}

		// No env vars referenced — return empty status for all environments
		// and workspace-only profiles.
		if (required.length === 0) {
			const result: DocumentRequiredVarsStatus = {};
			for (const envName of environmentNames) {
				result[envName] = { required: [], resolved: [], missing: [] };
			}
			return result;
		}

		const result: DocumentRequiredVarsStatus = {};

		for (const envName of environmentNames) {
			const globalVars = await get($environmentVars(envName));
			const rawDocVars = await get(
				$documentEnvironmentRawVars({ documentId, environmentName: envName })
			);
			const secretsContext = await get(
				$documentSecretsproxyContext({ documentId, environmentName: envName })
			);
			const secretNamesState = secretsContext
				? await get(
						$workspaceSecretNamesState({
							workspaceSlug: secretsContext.workspace_slug,
							profile: secretsContext.profile,
						})
					)
				: undefined;
			const secretNames = new Set(
				secretNamesState?.status === "ok" ? secretNamesState.names : []
			);
			const secretNamesUnknown = !!secretsContext && secretNamesState?.status !== "ok";
			// Doc vars override global vars — including empty values.
			// Matches $resolvedDocumentEnvironmentVars which merges all doc vars.
			const envVars = { ...globalVars, ...rawDocVars };

			const resolved: string[] = [];
			const missing: string[] = [];

			for (const varName of required) {
				if (varName in envVars && envVars[varName] !== "") {
					resolved.push(varName);
				} else if (secretNames.has(varName)) {
					resolved.push(varName);
				} else if (secretNamesUnknown) {
					continue;
				} else {
					missing.push(varName);
				}
			}

			result[envName] = { required: [...required], resolved, missing };
		}

		return result;
	});
	statusAtom.debugLabel = `documentRequiredVars/${documentId}`;
	return statusAtom;
});

/**
 * Aggregates env var hints across all request pages in a document.
 * Returns a Record mapping variable name to its most specific hint.
 * Merge strategy: most specific hint wins (kubecontext > string).
 */
export const $documentEnvVarHints = atomFamily((documentId: string) => {
	const hintsAtom = atom((get): Record<string, EnvVarHint> => {
		const metadataKey = metadataKeyForDocsRoute(documentId);
		const metadata = get($documentMetadata(metadataKey));

		if (!metadata) {
			return {};
		}

		const hints: Record<string, EnvVarHint> = {};

		for (const page of metadata.pagesMetadata) {
			if (page.type !== "request") {
				continue;
			}

			const requestPageKey = requestPageKeyForDocsRoute({ documentId, pageId: page.id });
			const requestPage = get($httpRequestPage(requestPageKey));

			if (!requestPage?.request?.request) {
				continue;
			}

			for (const ref of findEnvVarReferencesWithHints(requestPage.request.request)) {
				const existing = hints[ref.name];
				if (!existing || existing === "string") {
					hints[ref.name] = ref.hint;
				}
			}
		}

		return hints;
	});
	hintsAtom.debugLabel = `documentEnvVarHints/${documentId}`;
	return hintsAtom;
});
